import { store } from '@redux/store';
import { selectModal } from 'layout/redux/selectors';
import {
  decryptData,
  encryptData
} from 'utils/crypto';

const cryptoKeyName = "CryptoKey";
const preFillFormKey = "PrefillForm";

const setSelectValueAndDispatchChange = (selectInput, value) => {
  if (!selectInput || value === null || value === undefined || value === '') {
    return;
  }

  const normalizedValue = String(value);

  const options = Array.from(selectInput.options);
  const optionExists = options.some((option) => option.value === normalizedValue);

  if (!optionExists) {
    return;
  }

  selectInput.value = normalizedValue;
  selectInput.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Retrieves and decrypts prefill form data from local storage.
 * 
 * This function fetches encrypted form data and its corresponding crypto key from localStorage,
 * then decrypts the data using the AES-GCM algorithm. If the key or data is missing, or if
 * decryption fails, it returns an empty object.
 * 
 * @async
 * @function getLocalStoragePrefillFormData
 * @returns {Promise<Object>} A promise that resolves to the decrypted form data object,
 *                            or an empty object if data is unavailable or decryption fails.
 * @throws {Error} Silently catches and handles any decryption errors by returning an empty object.
 * 
 * @example
 * const formData = await getLocalStoragePrefillFormData();
 * console.log(formData); // { name: "John", email: "john@zondahome.com", ... }
 */
export const getLocalStoragePrefillFormData = async () => {
  const storedKey = localStorage.getItem(cryptoKeyName);
  const storedData = localStorage.getItem(preFillFormKey);
  if (!storedKey || !storedData) {
    return {};
  }

  try {
    const importedKey = await crypto.subtle.importKey(
      "jwk",
      JSON.parse(storedKey),
      { name: "AES-GCM" },
      true,
      ["encrypt", "decrypt"]
    );

    const { cipher, iv } = JSON.parse(storedData);

    const userInformation = await decryptData(cipher, iv, importedKey);

    return {
      ...userInformation,
      preferredContactMethod: userInformation?.preferredContactMethod ?? null,
      realtorAnswerId: userInformation?.realtorAnswerId ?? null
    };
  } catch (_) {
    return {};
  }
}

/**
 * Prefills a lead generator form with encrypted data stored in localStorage.
 * 
 * This function retrieves encrypted form data and a cryptographic key from localStorage,
 * decrypts the data using AES-GCM encryption, and populates the lead generator form fields
 * with the decrypted values. The function only executes if the current modal is 'leadGenerator'.
 * 
 * @async
 * @function prefillForm
 * @returns {Promise<void>} A promise that resolves when the form is prefilled or early returns occur.
 * 
 * @description
 * The function performs the following steps:
 * 1. Checks if the current modal is 'leadGenerator', returns early if not
 * 2. Retrieves the encryption key and encrypted data from localStorage
 * 3. Imports the cryptographic key for AES-GCM decryption
 * 4. Decrypts the stored form data
 * 5. Populates form fields (name, email, phone, zip, livesOutside) with decrypted values
 * 
 * Form fields populated:
 * - Full name (data-form-name-input)
 * - Email address (data-form-email-input)
 * - Phone number (data-form-phone-input)
 * - Postal code (data-form-zip-input)
 * - Lives outside checkbox (data-form-lives-outside-input)
 * 
 * @throws {Error} Silently catches and ignores all errors during decryption or form population
 */
export const prefillForm = async () => {
  const modalInformation = selectModal(store.getState());

  if (modalInformation?.name !== 'leadGenerator') {
    return;
  }

  try {
    const userInformation = await getLocalStoragePrefillFormData();
    const leadForm = document.querySelector('[data-lead-form]');

    if (!userInformation || !leadForm) { 
      return;
    }

    const fullNameInput       = leadForm.querySelector('[data-form-name-input]');
    const emailAddressInput   = leadForm.querySelector('[data-form-email-input]');
    const phoneNumberInput    = leadForm.querySelector('[data-form-phone-input]');
    const postalCodeInput     = leadForm.querySelector('[data-form-zip-input]');
    const livesOutsideInput   = leadForm.querySelector('[data-form-lives-outside-input]');
    const preferredContactMethodInput = leadForm.querySelector('[data-contact-method]');
    const realtorAnswerInput = leadForm.querySelector('[data-realtor-answer]');

    if (fullNameInput) {
      fullNameInput.value = userInformation.name || '';
    }

    if (emailAddressInput) {
      emailAddressInput.value = userInformation.email || '';
    }

    if (phoneNumberInput) {
      phoneNumberInput.value = userInformation.phone || '';
    }

    if (postalCodeInput) {
      postalCodeInput.value = userInformation.zip || '';
    }

    if (livesOutsideInput) {
      livesOutsideInput.checked = userInformation.livesOutside;
      postalCodeInput.disabled = userInformation.livesOutside;
    }

    setSelectValueAndDispatchChange(preferredContactMethodInput, userInformation.preferredContactMethod);
    setSelectValueAndDispatchChange(realtorAnswerInput, userInformation.realtorAnswerId);
  } catch (_) {
    // intentionally ignored
  }
}

/**
 * Updates and encrypts user information in local storage.
 * 
 * Retrieves user basic information from the store, then either imports an existing
 * encryption key from local storage or generates a new AES-GCM 256-bit key.
 * The user information is encrypted using this key and stored in local storage.
 * If a new key is generated, it is exported and stored in local storage for future use.
 * 
 * @async
 * @function updateUserInformation
 * @returns {Promise<void>} A promise that resolves when the user information has been
 * encrypted and stored, or rejects silently if an error occurs.
 * @throws {Error} Errors are caught and intentionally ignored.
 */
export const updateUserInformation = async (userInformation) => {
  try {
    const storedKey = localStorage.getItem(cryptoKeyName);
    const parsed = storedKey ? JSON.parse(storedKey) : null;

    const key = parsed
      ? await crypto.subtle.importKey(
          "jwk",
          parsed,
          { name: "AES-GCM" },
          true,
          ["encrypt", "decrypt"]
        )
      : await crypto.subtle.generateKey(
          { name: "AES-GCM", length: 256 },
          true,
          ["encrypt", "decrypt"]
        );

    if (!parsed) {
      const exported = await crypto.subtle.exportKey("jwk", key);
      localStorage.setItem(cryptoKeyName, JSON.stringify(exported));
    }

    const storedUserInformation = await getLocalStoragePrefillFormData();
    const normalizedUserInformation = Object.fromEntries(
      Object.entries(userInformation || {}).filter(([, value]) => value !== undefined)
    );
    const mergedUserInformation = {
      ...storedUserInformation,
      ...normalizedUserInformation
    };
    const encrypted = await encryptData(mergedUserInformation, key);
    localStorage.setItem(preFillFormKey, JSON.stringify(encrypted));

  } catch (_) {
    // intentionally ignored
  }
};

/**
 * Handles user authentication event to update user information.
 *
 * @async
 * @function onAuthenticatedUser
 * @param {CustomEvent} event - The event object containing user data.
 * @returns {Promise<void>} A promise that resolves when user information is updated.
 */
const onAuthenticatedUser = async (event) => {
  const userData = event.detail?.userData;
  if (!userData) {
    return;
  }

  const userInformation = {
    email: userData.Email || '',
    name: userData.FirstName + ' ' + userData.LastName,
    zip: userData.PostalCode || '',
    phone: '',
    livesOutside: false,
  };

  await updateUserInformation(userInformation);
};

document.body.addEventListener('loginSuccess', onAuthenticatedUser);